import { TIME_GROUP } from '../../common';
import { AUDIENCE_CSV_ID_TYPE, AUDIENCE_STATUS, AUDIENCE_TYPE } from '../constants/audiences.constant';
import { PERFORMANCE_REPORT_AUDIENCE_KPI_TYPE } from '../constants/performance-report.constant';
import { SEGMENT_FILTER_TYPE } from '../constants/segments.constant';
import { Segment } from '../models';
import {
  AudienceAdobe,
  AudienceAny,
  AudienceBase,
  AudienceCsv,
  AudienceCsvDynamic,
  AudienceExternal,
  AudienceFtp,
  AudienceHttps,
  AudiencePerformanceReport,
  AudienceS3,
  AudienceSegment,
} from '../models/audiences.model';
import { isAudienceSegment } from './audiences.guard';
import { getDefaultSegment } from './filters.helper';

export function getDefaultAudience(type: AUDIENCE_TYPE.CSV): AudienceCsv;
export function getDefaultAudience(type: AUDIENCE_TYPE.CSV_DYNAMIC): AudienceCsvDynamic;
export function getDefaultAudience(type: AUDIENCE_TYPE.SEGMENT): AudienceSegment;
export function getDefaultAudience(type: AUDIENCE_TYPE.EXTERNAL): AudienceExternal;
export function getDefaultAudience(type: AUDIENCE_TYPE.ADOBE): AudienceAdobe;
export function getDefaultAudience(type: AUDIENCE_TYPE.FTP): AudienceFtp;
export function getDefaultAudience(type: AUDIENCE_TYPE.HTTPS): AudienceHttps;
export function getDefaultAudience(type: AUDIENCE_TYPE.S3): AudienceS3;
export function getDefaultAudience(type: AUDIENCE_TYPE.PERFORMANCE_REPORT): AudiencePerformanceReport;
export function getDefaultAudience(): AudienceBase;
export function getDefaultAudience(type?: AUDIENCE_TYPE): AudienceAny {
  const base = { name: '', status: AUDIENCE_STATUS.NEW };
  switch (type) {
    case AUDIENCE_TYPE.CSV:
      return { ...base, type, csvContent: '' } as AudienceCsv;
    case AUDIENCE_TYPE.CSV_DYNAMIC:
      return {
        ...base,
        type,
        csvContent: '',
        csvIdType: AUDIENCE_CSV_ID_TYPE.CUSTOMER_ID,
      } as AudienceCsvDynamic;
    case AUDIENCE_TYPE.SEGMENT:
      return { ...base, type, segment: getDefaultSegment() } as AudienceSegment;
    case AUDIENCE_TYPE.ADOBE:
      return { ...base, type, adobeId: '' } as AudienceAdobe;
    case AUDIENCE_TYPE.FTP:
      return {
        ...base,
        type,
        ftpIdType: '',
        ftpHost: '',
        ftpPort: 21,
        ftpUsername: '',
        ftpPassword: '',
        ftpFileRoute: '',
        isSecureFtp: false,
      } as AudienceFtp;
    case AUDIENCE_TYPE.HTTPS:
      return { ...base, type, httpsIdType: '', httpsFileUrl: '' } as AudienceHttps;
    case AUDIENCE_TYPE.S3:
      return { ...base, type, s3FileRoute: '', s3IdType: '', filterTags: [], s3IntegrationId: 0 };
    case AUDIENCE_TYPE.PERFORMANCE_REPORT:
      return {
        ...base,
        type,
        kpiType: PERFORMANCE_REPORT_AUDIENCE_KPI_TYPE.IMPACTS,
        filters: [],
        dateRange: { startDay: '', endDay: '', timeGroup: TIME_GROUP.DAY },
        filterTags: [],
      };
    case AUDIENCE_TYPE.EXTERNAL:
      return { ...base, type } as AudienceExternal;
    default:
      return { ...base, type: AUDIENCE_TYPE.NONE } as AudienceBase;
  }
}

export const isSegmentWithAudienceExternal =
  (audiences: AudienceExternal[]) =>
  (segment: Segment): boolean =>
    segment.config.some((filter) => {
      if (filter.type === SEGMENT_FILTER_TYPE.ATTRIBUTE && filter.id === 'user_audience') {
        const audienceId = filter.multiSelect?.[0];
        return audiences.some((a) => a.id === audienceId);
      }
      return false;
    });

export const isAudienceSegmentWithAudienceExternal = (audiences: AudienceExternal[]) => {
  const hasAudienceExternal = isSegmentWithAudienceExternal(audiences);
  return (audience: AudienceAny): boolean =>
    isAudienceSegment(audience) && hasAudienceExternal(audience.segment);
};
